// ITR mapping: where each question lands on the Australian tax return.
//
// This is the layer that turns a questionnaire into something a junior
// accountant can work from. Without it the export is a list of answers; with
// it, every answer and every receipt says which box on the return it belongs
// in, and which field in the practice software to type it into.
//
// Keyed by question CODE, not by row id, so a mapping written once survives
// every future template year that reuses the code. See question.ts.
//
// ---------------------------------------------------------------------------
// Where these values came from
// ---------------------------------------------------------------------------
// The item numbers and label letters were verified line by line against a real
// prepared 2025 individual return, not inferred from the firm's paper
// checklist. That matters because the two disagree: the checklist implies a
// structure the lodged return does not have, and the spouse block in
// particular reuses letters that mean something different elsewhere on the
// form. A junior reading this column needs the letters the preparer actually
// types.
import { z } from 'zod';
import { entityTypeSchema, questionCodeSchema } from './question.js';
/**
 * Which return or schedule an answer belongs to.
 *
 * Open-ended on purpose — `form` is a string rather than an enum because new
 * schedules appear and the firm may add its own working papers. The known
 * values are listed as a hint for authors, not a constraint.
 */
export const KNOWN_FORMS = [
    'ITR',
    'Rental Schedule',
    'CGT Schedule',
    'Business Schedule',
    'Depreciation Schedule',
];
export const itrMappingSchema = z.object({
    questionCode: questionCodeSchema,
    entityType: entityTypeSchema.default('individual'),
    /** 'ITR', 'Rental Schedule', 'CGT Schedule', ... */
    form: z.string().min(1).default('ITR'),
    /**
     * Item number as printed on the return: '1', 'D5', 'IT8', 'P1', 'Spouse'.
     * '—' means the answer has no place on the return and is working-paper only
     * (client notes, unclassified evidence, practice records like a Director ID).
     */
    itrItem: z.string().max(20).optional(),
    /**
     * Label letter within the item: 'C' in "item 1 label C".
     *
     * Often several, slash-separated, because one question can feed more than
     * one box — dividends fill S, T, U and V from a single answer.
     */
    itrLabel: z.string().max(40).optional(),
    /** What that box is called on the return. */
    itrDescription: z.string().optional(),
    /** Income tax law area, for grouping the export: 'Deductions — travel'. */
    lawArea: z.string().optional(),
    /** The corresponding field in the practice software, so a junior knows where to type it. */
    xpmField: z.string().optional(),
    notes: z.string().optional(),
});
export const itrMappingsSchema = z.array(itrMappingSchema).superRefine((rows, ctx) => {
    // One mapping per (code, entity type). Two would make the export
    // non-deterministic — it would depend on array order which box a receipt is
    // filed against.
    const seen = new Set();
    for (const row of rows) {
        const key = `${row.entityType}:${row.questionCode}`;
        if (seen.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate mapping for '${row.questionCode}' (${row.entityType})`,
            });
        }
        seen.add(key);
    }
});
/** Index mappings by question code for O(1) lookup while building an export. */
export function indexMappings(mappings) {
    return new Map(mappings.map((m) => [m.questionCode, m]));
}
/**
 * Human-readable destination, e.g. "ITR D5 label E" or "Rental Schedule 21".
 * Used in the staff review and as the export's at-a-glance column.
 */
export function describeDestination(mapping) {
    if (!mapping)
        return 'Unmapped';
    const parts = [mapping.form];
    if (mapping.itrItem && mapping.itrItem !== '—')
        parts.push(mapping.itrItem);
    if (mapping.itrLabel)
        parts.push(`label ${mapping.itrLabel}`);
    return parts.join(' ');
}
export function parseMappings(input) {
    const result = itrMappingsSchema.safeParse(input);
    if (!result.success) {
        const problems = result.error.issues
            .map((i) => `  - ${i.path.join('.') || '(root)'}: ${i.message}`)
            .join('\n');
        throw new Error(`ITR mappings are not valid:\n${problems}`);
    }
    return result.data;
}
//# sourceMappingURL=itr.js.map